import type { Invoice } from '@/types/invoice';
import { taxableSubtotalOf } from '@/lib/tax/tax-utils';
import type { TaxSnapshot, TaxBreakdownItem } from '@/types/tax';
import { documentDiscountLabel } from '@/lib/sales/document-discount-label';
import { customerClearingFootnote, shownInvoiceMoney } from './shown-invoice-money';

/**
 * The money an invoice DOCUMENT renders — the single derivation shared by the
 * templated invoice PDF (`InvoiceTemplatePdfDocument`) and its DOM twin
 * (`InvoiceDocumentRenderer`). Both surfaces read the SAME numbers from here, so
 * the customer's hosted invoice page and the invoice PDF can never disagree on a
 * figure — the exact drift the estimate document had before it went through one
 * shared money helper.
 */
export interface InvoiceDocMoney {
  subtotal: number;
  discountTotal: number;
  membershipDiscountTotal: number;
  discountLabel: string;
  taxTotal: number;
  /** Net amount of the lines that carry tax — see `taxableSubtotalOf`. */
  taxableSubtotal: number;
  taxBreakdown: Array<{
    label: string;
    amount: number;
    registrationNumber: string | null;
  }>;
  tip: number;
  /** Customer-facing amount represented by the invoice plus a voluntary tip. */
  totalWithTip: number;
  grandTotal: number;
  amountPaid: number;
  /**
   * The deposit portion of `amountPaid`. `amountPaid` ALREADY contains it, so
   * a document renders the two as a split of one figure — never as two
   * reductions, which would take the money off the balance twice on paper.
   */
  depositApplied: number;
  /** `amountPaid` minus the deposit: what was paid against the invoice itself. */
  paymentsExcludingDeposit: number;
  /** Card service fee charged on the deposits behind `depositApplied`. */
  depositServiceFee: number;
  /**
   * Card service fee charged on money paid against the invoice itself. Kept
   * apart from the deposit fee: they are different money, and the document
   * says which is which.
   */
  paymentServiceFee: number;
  creditsApplied: number;
  balanceDue: number;
  /**
   * The footnote for bank money still processing, without the leading "*";
   * null when none. `amountPaid`/`creditsApplied`/`balanceDue` above are the
   * SHOWN figures: that money counts as paid, exactly as it will once it
   * settles.
   */
  paidFootnote: string | null;
  terminalStatusLabel: string | null;
}

const persistedMoney = (v: unknown): number | null =>
  typeof v === 'number' && Number.isFinite(v) ? v : null;

const usd = (n: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);

/**
 * `timeZone` is the COMPANY's: it decides which calendar day the footnote
 * names. Unknown falls back to UTC so the day never depends on the viewer.
 */
export function computeInvoiceMoney(
  invoice: Invoice,
  promotionName?: string | null,
  timeZone?: string | null,
): InvoiceDocMoney {
  const subtotal = Number(invoice.subtotal) || 0;
  const discountTotal = Number(invoice.discountTotal) || 0;
  const membershipDiscountTotal = Number(invoice.membershipDiscountTotal) || 0;
  const taxTotal = Number(invoice.taxTotal) || 0;
  const tip = invoice.tipAmount ?? 0;
  // NOT-535 — the tip is deliberately NOT in `total`: a gratuity is paid on
  // top of the bill, not billed. The fallback has to agree with the persisted
  // value it stands in for, or an invoice missing `total` prints a Total that
  // already swallowed the tip and then lists the tip again below it.
  const computedTotal = subtotal - discountTotal - membershipDiscountTotal + taxTotal;
  const grandTotal = persistedMoney(invoice.total) ?? computedTotal;
  // A voluntary gratuity must remain outside the billed/taxable invoice total,
  // but the customer still needs one figure that reconciles to what was
  // charged. Keep both facts explicit instead of silently folding the tip into
  // the invoice balance.
  const totalWithTip = grandTotal + tip;
  const discountLabel = documentDiscountLabel({
    promotionName,
    promotionId: invoice.promotionId,
    discountReason: invoice.discountReason,
    discountType: invoice.discountType,
    discountValue: invoice.discountValue,
  });

  const byCode = new Map<
    string,
    { label: string; amount: number; registrationNumber: string | null }
  >();
  for (const li of invoice.lineItems) {
    const snap = (li as { taxSnapshot?: TaxSnapshot | null }).taxSnapshot;
    const entries = (snap?.breakdown ?? []) as TaxBreakdownItem[];
    for (const entry of entries) {
      const nm = (entry.name ?? '').trim();
      const jur = (entry.jurisdiction ?? '').trim();
      const label = nm && jur && nm !== jur ? `${nm} — ${jur}` : nm || jur || 'Tax';
      const existing = byCode.get(label);
      if (existing) {
        existing.amount += entry.amount;
        if (!existing.registrationNumber && entry.registrationNumber) {
          existing.registrationNumber = entry.registrationNumber;
        }
      } else {
        byCode.set(label, {
          label,
          amount: entry.amount,
          registrationNumber: entry.registrationNumber ?? null,
        });
      }
    }
  }
  // Per-jurisdiction rows come from each line's frozen `taxSnapshot`. A legacy
  // line can lack a snapshot, so the breakdown can under-count the real
  // `taxTotal` — and since the Total line uses `taxTotal`, showing the partial
  // rows would print a document that visibly does not add up
  // (subtotal − discounts + shown tax ≠ Total). When the rows don't reconcile to
  // `taxTotal`, drop them so the renderers fall back to one `Tax = taxTotal` row
  // (the classic invoice's always-footing behaviour).
  const breakdownRows = Array.from(byCode.values());
  const breakdownSum = breakdownRows.reduce((sum, row) => sum + row.amount, 0);
  const taxBreakdown = Math.abs(breakdownSum - taxTotal) <= 0.01 ? breakdownRows : [];
  // Shown figures: bank money still processing counts as paid (cash) and its
  // earned discount as credit, exactly as settlement will book them. A figure
  // the server withheld (null) keeps the stored value rather than inventing 0.
  const shown = shownInvoiceMoney(invoice);
  const amountPaid = shown.paid ?? invoice.amountPaid ?? 0;
  // Clamped to what was actually paid: the two rows a document prints from
  // these have to sum back to `amountPaid`, and a stale or hand-edited
  // `depositApplied` above it would otherwise print a negative Payments row.
  const depositApplied = Math.min(
    Math.max(invoice.depositApplied ?? 0, 0),
    Math.max(amountPaid, 0),
  );
  const paymentsExcludingDeposit = Math.max(0, amountPaid - depositApplied);
  const depositServiceFee = depositApplied > 0 ? (invoice.depositServiceFeeAmount ?? 0) : 0;
  // NOT gated on a paid amount the way the deposit fee is gated on
  // `depositApplied`: the server resolves this from the payment applications
  // themselves, so a non-zero value already means money was paid on the
  // invoice by card. Gating it on `paymentsExcludingDeposit` would blank it on
  // an invoice whose whole balance was settled by that very card payment.
  const paymentServiceFee = Math.max(0, invoice.paymentServiceFeeAmount ?? 0);
  const creditsApplied = shown.credits;
  const terminalStatusLabel =
    invoice.status === 'void' ? 'VOID' : invoice.status === 'written_off' ? 'WRITTEN OFF' : null;
  // A void / written-off invoice owes nothing — mirror the classic hosted page,
  // which forces the displayed balance to 0 on a terminal status so the document
  // never shows a customer a balance due on an invoice that was cancelled.
  const balanceDue = terminalStatusLabel
    ? 0
    : persistedMoney(invoice.balanceDue) !== null
      ? (shown.balance ?? persistedMoney(invoice.balanceDue) ?? 0)
      : Math.max(0, grandTotal - amountPaid - creditsApplied);
  const paidFootnote = terminalStatusLabel
    ? null
    : customerClearingFootnote(invoice, usd, timeZone || 'UTC');
  return {
    depositApplied,
    paymentsExcludingDeposit,
    depositServiceFee,
    paymentServiceFee,
    subtotal,
    discountTotal,
    membershipDiscountTotal,
    discountLabel,
    taxTotal,
    taxableSubtotal: taxableSubtotalOf(invoice.lineItems),
    taxBreakdown,
    tip,
    totalWithTip,
    grandTotal,
    amountPaid,
    creditsApplied,
    balanceDue,
    paidFootnote,
    terminalStatusLabel,
  };
}
